import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate, useParams } from 'react-router-dom';
import { PageHeader } from '@/shared/ui/layout/PageHeader';
import { Spinner } from '@/shared/ui/feedback/Spinner';
import { EmptyState } from '@/shared/ui/feedback/EmptyState';
import { Textarea } from '@/shared/ui/inputs/Textarea';
import { Button } from '@/shared/ui/buttons/Button';
import { useToast } from '@/shared/hooks/useToast';
import { extractErrorMessage } from '@/shared/api/errors';
import { useParcelsAvailableForReturn, useCreateReturn } from '@/entities/returns';
import { usePricingConfig } from '@/entities/pricing';
import { formatUsd } from '@/shared/utils/currency';

const backTo = '/services/return';

export const WidgetsServicesReturnFormMain = () => {
  const { parcelId } = useParams<{ parcelId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('services');
  const toast = useToast();

  const { data, isLoading } = useParcelsAvailableForReturn();
  const parcel = (data ?? []).find((p) => p.id === parcelId);

  const { data: pricing } = usePricingConfig();
  const fee = pricing?.return_fee;

  const schema = z.object({
    note: z.string().optional(),
  });
  type FormValues = z.infer<typeof schema>;

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { note: '' },
    mode: 'onBlur',
  });

  const createReturn = useCreateReturn();

  const onSubmit = form.handleSubmit((values) => {
    if (!parcelId) return;
    createReturn.mutate(
      {
        parcel: parcelId,
        note: values.note || undefined,
      },
      {
        onSuccess: () => {
          toast.success(t('returnCreated'));
          navigate(backTo);
        },
        onError: (err) => {
          toast.error(extractErrorMessage(err, t('returnError')));
        },
      },
    );
  });

  return (
    <>
      <PageHeader title={t('returnTitle')} showBack backTo={backTo} />
      <div className="flex flex-col gap-4 p-4 md:mx-auto md:w-full md:max-w-2xl lg:p-6">
        {isLoading ? (
          <div className="flex justify-center py-16">
            <Spinner size="lg" />
          </div>
        ) : !parcel ? (
          <EmptyState title={t('selectParcel')} />
        ) : (
          <>
            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-card">
              <p className="text-base font-semibold text-ink">{parcel.name}</p>
              <p className="mt-0.5 text-xs text-ink-muted">{parcel.trek_id}</p>
              <span className="mt-1.5 inline-flex rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-ink-muted">
                {t(`parcelStatus:${parcel.status}`)}
              </span>
            </div>

            {fee != null && (
              <div className="flex items-center justify-between rounded-2xl border border-slate-200 bg-white p-4 shadow-card">
                <span className="text-sm font-medium text-ink-muted">{t('returnFeeLabel')}</span>
                <span className="text-base font-semibold text-ink">{formatUsd(Number(fee))}</span>
              </div>
            )}

            <form onSubmit={onSubmit} className="flex flex-col gap-4">
              <Textarea label={t('returnReason')} {...form.register('note')} />
              <Button type="submit" loading={createReturn.isPending} block>
                {t('submit')}
              </Button>
            </form>
          </>
        )}
      </div>
    </>
  );
};
